export const TEXT_TEMPLATE_CATEGORIES = {
  transactional: 'Transactional',
  workflow: 'Workflow',
  marketing: 'Marketing',
  system: 'System',
}

export const WORKFLOW_TEMPLATE_CATEGORIES = ['transactional', 'workflow', 'marketing']

export const TEXT_TEMPLATE_VARIABLES = [
  { key: 'user.name', label: 'User Name', type: 'string', group: 'user', example: 'John Doe' },
  { key: 'user.firstName', label: 'User First Name', type: 'string', group: 'user', example: 'John' },
  { key: 'user.lastName', label: 'User Last Name', type: 'string', group: 'user', example: 'Doe' },
  { key: 'user.phone', label: 'User Phone', type: 'phone', group: 'user', example: '+15551234567' },
  { key: 'user.email', label: 'User Email', type: 'string', group: 'user', example: 'john@example.com' },
  { key: 'customer.name', label: 'Customer Name', type: 'string', group: 'customer', example: 'Jane Smith' },
  { key: 'customer.phone', label: 'Customer Phone', type: 'phone', group: 'customer', example: '+15559876543' },
  { key: 'customer.address', label: 'Customer Address', type: 'string', group: 'customer', example: '123 Main St' },
  { key: 'job.id', label: 'Job ID', type: 'string', group: 'job', example: 'JOB-12345' },
  { key: 'job.address', label: 'Job Address', type: 'string', group: 'job', example: '456 Oak Ave' },
  { key: 'job.status', label: 'Job Status', type: 'string', group: 'job', example: 'In Progress' },
  { key: 'appointment.date', label: 'Appointment Date', type: 'date', group: 'appointment', example: 'May 20, 2026' },
  { key: 'appointment.time', label: 'Appointment Time', type: 'string', group: 'appointment', example: '2:00 PM' },
  { key: 'company.name', label: 'Company Name', type: 'string', group: 'company', example: 'DME Platform' },
  { key: 'company.phone', label: 'Company Phone', type: 'phone', group: 'company', example: '+15550001111' },
  { key: 'link.url', label: 'Link URL', type: 'url', group: 'link', example: 'https://app.example.com' },
  { key: 'link.token', label: 'Link Token', type: 'string', group: 'link', example: 'abc123xyz' },
  { key: 'date.today', label: "Today's Date", type: 'date', group: 'date', example: 'May 20, 2026' },
  { key: 'date.time', label: 'Current Time', type: 'string', group: 'date', example: '2:00 PM' },
  { key: 'key', label: 'Unique Token/Key', type: 'string', group: 'link', example: 'abc123xyz' },
  { key: 'url', label: 'Application URL', type: 'url', group: 'link', example: 'https://app.example.com' },
  { key: 'app_name', label: 'Application Name', type: 'string', group: 'company', example: 'DME Platform' },
  { key: 'date', label: 'Current Year', type: 'date', group: 'date', example: '2026' },
]

const VARIABLES_BY_KEY = Object.fromEntries(TEXT_TEMPLATE_VARIABLES.map((v) => [v.key, v]))

export function getVariableMeta(key) {
  return VARIABLES_BY_KEY[key] || {
    key,
    label: key.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    type: 'string',
    group: key.split('.')[0] || 'general',
  }
}

export function formatVariableToken(key, legacy = false) {
  if (legacy) {
    return `{{ $${key} }}`
  }
  return `{{ ${key} }}`
}

export function parseVariablesFromContent(content) {
  if (!content) return []

  const found = new Map()

  const semanticRegex = /\{\{\s*([a-zA-Z][a-zA-Z0-9_.]*)\s*\}\}/g
  let match
  while ((match = semanticRegex.exec(content)) !== null) {
    const key = match[1]
    if (!key.startsWith('$')) {
      found.set(key, getVariableMeta(key))
    }
  }

  const legacyRegex = /\{\{\s*\$([a-zA-Z_][a-zA-Z0-9_]*)(?:->|\.)?([a-zA-Z0-9_]*)?\s*\}\}/g
  while ((match = legacyRegex.exec(content)) !== null) {
    const root = match[1]
    const prop = match[2]
    const key = prop ? `${root}.${prop}` : root
    if (!found.has(key)) {
      found.set(key, getVariableMeta(key))
    }
  }

  return Array.from(found.values())
}

export function getSampleVariableData() {
  return {
    'user.name': 'John Doe',
    'user.firstName': 'John',
    'user.lastName': 'Doe',
    'user.phone': '+15551234567',
    'user.email': 'john@example.com',
    'customer.name': 'Jane Smith',
    'customer.phone': '+15559876543',
    'customer.address': '123 Main St',
    'job.id': 'JOB-12345',
    'job.address': '456 Oak Ave',
    'job.status': 'In Progress',
    'appointment.date': 'May 20, 2026',
    'appointment.time': '2:00 PM',
    'company.name': 'DME Platform',
    'company.phone': '+15550001111',
    'link.url': typeof window !== 'undefined' ? window.location.origin : 'https://app.example.com',
    'link.token': 'sample-key-123',
    'date.today': 'May 20, 2026',
    'date.time': '2:00 PM',
    key: 'sample-key-123',
    url: typeof window !== 'undefined' ? window.location.origin : 'https://app.example.com',
    app_name: 'DME Platform',
    date: new Date().getFullYear().toString(),
  }
}

export function renderPreviewContent(content, sampleData = getSampleVariableData()) {
  if (!content) return ''

  let preview = content
  Object.entries(sampleData).forEach(([key, value]) => {
    const escaped = key.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
    preview = preview.replace(new RegExp(`\\{\\{\\s*\\$${escaped.replace('.', '->')}\\s*\\}\\}`, 'g'), value)
    preview = preview.replace(new RegExp(`\\{\\{\\s*${escaped}\\s*\\}\\}`, 'g'), value)
  })

  return preview
}

const HTML_EMAIL_TAG_PATTERN =
  /<\s*(?:p|br|div|table|ul|ol|li|h[1-6]|html|body|span|a|img|strong|em|b|i|u|s|strike|del|code|pre|sub|sup|thead|tbody|tr|td|th|blockquote|hr)\b/i

export function looksLikeHtmlEmail(content) {
  return HTML_EMAIL_TAG_PATTERN.test(content || '')
}

/**
 * Convert plain-text email body to HTML paragraphs for preview/send parity.
 */
export function formatPlainTextAsHtmlEmail(content) {
  if (!content) return ''

  const normalized = String(content).replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  if (!normalized.trim() || looksLikeHtmlEmail(normalized)) {
    return content
  }

  const escapeHtml = (value) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  const spacerParagraph = () => '<p><br></p>'
  const textParagraph = (lines) => `<p>${lines.map((line) => escapeHtml(line)).join('<br>')}</p>`

  const lines = normalized.split('\n')
  const htmlParts = []
  let index = 0

  while (index < lines.length) {
    if (lines[index].trim() !== '') {
      const paragraphLines = []
      while (index < lines.length && lines[index].trim() !== '') {
        paragraphLines.push(lines[index])
        index += 1
      }
      htmlParts.push(textParagraph(paragraphLines))
      continue
    }

    let blankLineCount = 0
    while (index < lines.length && lines[index].trim() === '') {
      blankLineCount += 1
      index += 1
    }

    const spacerCount = htmlParts.length === 0 ? blankLineCount : Math.max(0, blankLineCount - 1)
    for (let i = 0; i < spacerCount; i += 1) {
      htmlParts.push(spacerParagraph())
    }
  }

  return htmlParts.join('\n')
}

export function estimateSmsSegments(message) {
  if (!message) return 0
  const isUnicode = /[^\x00-\x7F]/.test(message)
  const singleLimit = isUnicode ? 70 : 160
  const multiLimit = isUnicode ? 67 : 153
  const length = [...message].length
  if (length <= singleLimit) return 1
  return Math.ceil(length / multiLimit)
}

export function suggestVariableMapping(slotKey, upstreamVariables = []) {
  const slotParts = slotKey.toLowerCase().split(/[._]/)
  let best = null
  let bestScore = 0

  upstreamVariables.forEach((variable) => {
    const name = (variable.name || variable).toLowerCase()
    const nameParts = name.split(/[._]/)
    let score = 0
    slotParts.forEach((part) => {
      if (nameParts.some((np) => np.includes(part) || part.includes(np))) {
        score += 1
      }
    })
    if (name === slotKey.toLowerCase()) score += 3
    if (score > bestScore) {
      bestScore = score
      best = typeof variable === 'string' ? variable : variable.name
    }
  })

  return bestScore > 0 && best ? `{{${best}}}` : ''
}
